import { useCallback, useMemo, useState } from "react"
import { useIntl } from "react-intl"

import { formatPnr } from "@doktor-se/bones-ui/dist/web-shared/utils"

import { useAppSelector } from "lib/hooks"
import { BookedAppointment } from "reducers/conversations/conversations.reducer"

import { AssignedStaffCell, BooleanCell, DateCell, NameCell } from "components/v2/conversationList/Cells"
import ReassignDialog from "components/v2/conversationList/components/ReassignDialog/ReassignDialog"
import ConversationTable, { ConversationListItem } from "components/v2/conversationList/ConversationTable"
import { createSortValues, sortValueFunctions } from "components/v2/conversationList/sortUtils"

interface BookedErrandsListProps {
  title: string
  conversations: BookedAppointment[]
}

interface BookedErrandsListItem extends ConversationListItem<BookedAppointment> {}

const BookedErrandsList = ({ title, conversations }: BookedErrandsListProps) => {
  const intl = useIntl()
  const kinds = useAppSelector(state => state.booking.bookingTypes)
  const appLanguage = useAppSelector(state => state.app.language)
  const { activeStaff } = useAppSelector(state => state.users)
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false)
  const [selectedConversation, setSelectedConversation] = useState<BookedAppointment>()

  const closeReassignDialog = () => {
    setSelectedConversation(undefined)
    setReassignDialogOpen(false)
  }

  const columns = [
    {
      key: "pnr",
      name: intl.formatMessage({ id: "label.overview.pnr" })
    },
    {
      key: "name",
      name: intl.formatMessage({ id: "label.overview.name" })
    },
    {
      key: "kind",
      name: intl.formatMessage({ id: "label.overview.booking_type" })
    },
    {
      key: "start",
      name: intl.formatMessage({ id: "label.overview.booked_time" })
    },
    {
      key: "listed",
      name: intl.formatMessage({ id: "label.overview.listed" })
    },
    {
      key: "assigned",
      name: intl.formatMessage({ id: "label.overview.assigned" })
    },
    {
      key: "recordConsent",
      name: intl.formatMessage({ id: "label.overview.medical_consent" })
    },
    {
      key: "medicationConsent",
      name: intl.formatMessage({ id: "label.overview.medication_consent" })
    }
  ]

  const mapConversationListItem = useCallback(
    (conv: BookedAppointment): BookedErrandsListItem => {
      const isChildConversation = conv.profile?.type === "child"
      const { kind_id: kindId } = JSON.parse(conv.bookingFilter)
      return {
        conversation: conv,
        values: {
          pnr: isChildConversation
            ? formatPnr(conv.profile!.data!.securityNumber, conv.metadata?.region)
            : formatPnr(conv.account?.pnr, conv.metadata?.region),
          name: (
            <NameCell
              name={isChildConversation ? conv.profile!.name! : conv.account.displayName!}
              isChild={isChildConversation}
            />
          ),
          kind: kinds.find(k => k.id === kindId)?.localisedNames[appLanguage],
          start: <DateCell value={conv.bookingStart} />,
          listed: <BooleanCell value={!!conv.customerMetadata?.listedAtClinicName} />,
          assigned: <AssignedStaffCell staff={activeStaff} staffId={conv.assignedStaffId} />,
          recordConsent: <BooleanCell value={!!conv.customerMetadata?.otherJournal} />,
          medicationConsent: <BooleanCell value={!!conv.customerMetadata?.medicationList} />
        },
        sortValues: {
          ...createSortValues(conv, ["pnr", "name", "listed", "recordConsent", "medicationConsent", "assigned"], {}),
          kind: sortValueFunctions.bookingType(conv, { bookingTypes: kinds, appLanguage }),
          start: sortValueFunctions.bookingStart(conv)
        }
      }
    },
    [activeStaff, appLanguage, kinds]
  )

  const list = useMemo(
    () => conversations.map(conv => mapConversationListItem(conv)),
    [conversations, mapConversationListItem]
  )

  const openReassignDialog = (item: BookedErrandsListItem) => {
    setSelectedConversation(item.conversation)
    setReassignDialogOpen(true)
  }

  return (
    <>
      <ConversationTable title={title} columns={columns} items={list} onItemClick={openReassignDialog} />
      {reassignDialogOpen && selectedConversation && (
        <ReassignDialog
          isOpen={reassignDialogOpen}
          conversation={selectedConversation!}
          onClose={closeReassignDialog}
        />
      )}
    </>
  )
}

export default BookedErrandsList
